import { Money, formatMoney, toMoney } from '@betterment/core';
import { Box, Separator, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';

import { DataPoint } from './components/DataPoint';

import { urlBuilder } from '../../../../Shared/urlBuilder';
import { EnvelopeAccountHeaderQuery } from '../graphql/envelopeAccountHeader.generated';

type CashAccountOverviewProps = {
  accountId: string;
  apy: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['apy'];
  balance: Money;
  fdicInsurance: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['fdicInsurance'];
  performanceTotals: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['performanceTotals'];
  legacyGoalId: Extract<EnvelopeAccountHeaderQuery['account'], { __typename: 'CashAccount' }>['legacyGoalId'];
};

export const CashAccountOverview = ({
  accountId,
  apy,
  balance,
  fdicInsurance,
  performanceTotals,
  legacyGoalId,
}: CashAccountOverviewProps) => {
  const { enabled: isReactifyPerformanceEnabled, loading: isReactifyPerformanceLoading } = useFeatureFlag({
    splitName: 'retail.user_level_performance_reactify_q3_2025_enabled',
    context: 'PurposePerformanceCard',
  });

  if (isReactifyPerformanceLoading) return <CardLoader />;

  const { interest } = performanceTotals;

  const performanceUrl = isReactifyPerformanceEnabled
    ? urlBuilder.appAccountPerformance.absolute({
        accountId,
        returnTo: urlBuilder.appAccount.absolute({ accountId }),
        returnLabel: 'Account details',
      })
    : urlBuilder.appPerformance.absolute({ 'settings[goal_ids][]': legacyGoalId });

  return (
    <Box css={{ display: 'grid', gap: 'space.300' }}>
      <Separator />
      <Box css={{ display: 'grid' }}>
        <Text>Balance</Text>
        <Text variant="text400">{formatMoney(balance)}</Text>
      </Box>
      <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
        <DataPoint
          label="Current APY*"
          value={`${apy.rate}%`}
          explanation={apy.explanation}
          isBoostedActive={apy.isBoostActive}
        />
        <DataPoint label="FDIC Insurance" value={fdicInsurance.amount} explanation={fdicInsurance.explanation} />
        <DataPoint
          label="Total interest paid"
          value={formatMoney((interest?.paid as Money) ?? toMoney(0))}
          valueColor={(interest?.paid ?? toMoney(0)).cents === 0 ? 'text' : 'text.success'}
        />
        <DataPoint
          label="Interest to be paid"
          value={formatMoney((performanceTotals.interest?.accrued as Money) ?? toMoney(0))}
          valueColor={(interest?.accrued ?? toMoney(0)).cents === 0 ? 'text' : 'text.success'}
          explanation={interest?.accruedExplanation}
        />
      </Box>
      <Separator />
      <SambaAutoLink to={performanceUrl}>See balance history</SambaAutoLink>
    </Box>
  );
};
